/**
 * Department mode (docs/plans/departments.md, "Department mode"): the task carries the department,
 * not the project. A project lives in every department one of its tasks is in; a project with no
 * tasks yet lives in all of them. `task.departmentId` unset, null or unknown = General.
 * Subtasks have no department of their own — they follow their top-level task.
 *
 * Experimental: on when meta/settings.departmentMode is true. Off, the app is unchanged and
 * `project.departmentId` (hierarchy.js) is what the screens show. Either way that project field still
 * drives the clock / GPS / books / billable rules until D5. Pure — no I/O.
 */
import { GENERAL_DEPARTMENT_ID, listDepartments } from './hierarchy.js';
import { update } from './commands/writes.js';
import { carriedDays } from './dayplan.js';

/** The switcher's "All" value. */
export const ALL_DEPARTMENTS = 'all';

export const isDepartmentMode = (settings) => settings?.departmentMode === true;

const isOpen = (t) => t && t.status !== 'done' && !t.deletedAt;

/**
 * A project locked to one department (v0.1.226, Lj: "Battle Ops doesn't need to be across every department — it
 * can just be Dev"): `project.departmentLock === true` and every task in it takes the project's department
 * (`departmentId`, null = General). Unlocked (the default) = global: each task carries its own.
 */
export const isDepartmentLocked = (project) => project?.departmentLock === true;

/** projectId → the department its tasks are locked to (only locked projects). */
export function lockedProjectDepartments(projects = [], departments = []) {
  const known = new Set(listDepartments(departments).map((d) => d.id));
  const out = new Map();
  for (const p of projects || []) if (p?.id && isDepartmentLocked(p)) out.set(p.id, p.departmentId && known.has(p.departmentId) ? p.departmentId : GENERAL_DEPARTMENT_ID);
  return out;
}

/**
 * task → its department id. Build once per list: `const deptOfTask = taskDepartmentResolver(tasks, departments, projects)`.
 * Walks up to the top-level task; an id that isn't a department (deleted, or never was) reads as General.
 * A project locked to a department wins over the task's own (pass `projects` for that).
 */
export function taskDepartmentResolver(tasks = [], departments = [], projects = []) {
  const byId = new Map(tasks.filter((t) => t && t.id).map((t) => [t.id, t]));
  const known = new Set(listDepartments(departments).map((d) => d.id));
  const locks = lockedProjectDepartments(projects, departments);
  return (task) => {
    let t = task;
    for (let i = 0; t?.parentId && i < 32; i++) {
      const up = byId.get(t.parentId);
      if (!up) break;
      t = up;
    }
    const locked = (t?.projectId && locks.get(t.projectId)) || (task?.projectId && locks.get(task.projectId));
    if (locked) return locked;
    const id = t?.departmentId;
    return id && known.has(id) ? id : GENERAL_DEPARTMENT_ID;
  };
}

/** projectId → Set of the departments its tasks are in (every task, done or not). Projects with no tasks have no entry. */
export function projectDepartmentIndex(tasks = [], deptOfTask) {
  const out = new Map();
  for (const t of tasks) {
    if (!t?.projectId || t.deletedAt) continue;
    if (!out.has(t.projectId)) out.set(t.projectId, new Set());
    out.get(t.projectId).add(deptOfTask(t));
  }
  return out;
}

/** Does the project show in this department? All → yes; no tasks yet → yes (so a new project isn't lost). */
export function projectInDepartment(projectId, departmentId, index) {
  if (!departmentId || departmentId === ALL_DEPARTMENTS) return true;
  const set = index.get(projectId);
  return !set || set.size === 0 || set.has(departmentId);
}

export function taskInDepartment(task, departmentId, deptOfTask) {
  if (!departmentId || departmentId === ALL_DEPARTMENTS) return true;
  return deptOfTask(task) === departmentId;
}

/** Open tasks per department: Map id → count (General included, every department present). */
export function departmentOpenCounts(tasks = [], departments = [], projects = []) {
  const deptOfTask = taskDepartmentResolver(tasks, departments, projects);
  const out = new Map(listDepartments(departments).map((d) => [d.id, 0]));
  for (const t of tasks) if (isOpen(t)) out.set(deptOfTask(t), (out.get(deptOfTask(t)) || 0) + 1);
  return out;
}

/**
 * Move tasks to a department (null / 'general' = General). Only top-level tasks carry it — a subtask in
 * `ids` is skipped (it follows its parent), and so is a task whose project is locked to another department
 * (ctx.projects; move it to another project instead). Returns { writes, moved, skipped, locked }.
 */
export function moveTasksToDepartment(ctx, { ids = [], departmentId = null }) {
  const at = typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now();
  const value = !departmentId || departmentId === GENERAL_DEPARTMENT_ID ? null : departmentId;
  const byId = new Map((ctx.tasks || []).map((t) => [t.id, t]));
  const lockedTo = new Map((ctx.projects || []).filter(isDepartmentLocked).map((p) => [p.id, p.departmentId || null]));
  const writes = [];
  let skipped = 0;
  let locked = 0;
  for (const id of new Set(ids)) {
    const t = byId.get(id);
    if (!t) continue;
    if (t.parentId) { skipped++; continue; }
    if (t.projectId && lockedTo.has(t.projectId) && (lockedTo.get(t.projectId) || null) !== value) { locked++; continue; }
    if ((t.departmentId || null) === value) continue;
    writes.push(update(`tasks/${id}`, { departmentId: value, updatedAt: at }));
  }
  return { writes, moved: writes.length, skipped, locked };
}

/**
 * The first time department mode is turned on: each top-level task with no department of its own takes
 * its project's department (when that's a real department other than General). The project's field is
 * left alone, so turning the mode off changes nothing. Returns the task writes.
 */
export function seedTaskDepartments({ projects = [], tasks = [], departments = [], now = Date.now() }) {
  const known = new Set(listDepartments(departments).map((d) => d.id));
  const projectDept = new Map(projects.filter((p) => p?.id).map((p) => [p.id, p.departmentId]));
  const writes = [];
  for (const t of tasks) {
    if (!t?.id || t.parentId || t.deletedAt || t.departmentId !== undefined) continue;
    const d = projectDept.get(t.projectId);
    if (!d || d === GENERAL_DEPARTMENT_ID || !known.has(d)) continue;
    writes.push(update(`tasks/${t.id}`, { departmentId: d, updatedAt: now }));
  }
  return writes;
}

// ---- the switcher (D2) -------------------------------------------------------------------------------------

/**
 * What the switcher offers this member. access null (an admin, or no grants) = All + every department.
 * With department grants: only those; one → locked to it (no arrows, no list). Project-only grants → All, locked.
 * @returns {{ options: Array<{ id, name, icon, color }>, locked: boolean }}
 */
export function departmentChoices(departments = [], access = null) {
  const list = listDepartments(departments).map((d) => ({ id: d.id, name: d.name, icon: d.icon, color: d.color }));
  const all = { id: ALL_DEPARTMENTS, name: 'All', icon: '◎', color: null };   // "All Deps" → "All" (Lj, Oct 11)
  if (!access) return { options: [all, ...list], locked: false };
  const granted = list.filter((d) => access.departments?.[d.id]);
  if (granted.length === 1) return { options: granted, locked: true };
  if (granted.length === 0) return { options: [all], locked: true };
  return { options: [all, ...granted], locked: false };
}

/** The department in force: the device's pick when it's on offer, else the first option. */
export function effectiveDepartment(options, current) {
  return options.find((o) => o.id === current) || options[0] || null;
}

/** ‹ / › on the switcher: the previous / next option, wrapping. */
export function stepDepartment(options, current, dir) {
  if (!options.length) return null;
  const i = Math.max(0, options.findIndex((o) => o.id === current));
  return options[(i + (dir < 0 ? -1 : 1) + options.length) % options.length].id;
}

/**
 * Hold-to-sync: meta/settings.departmentSync = { id, at, deviceId }. A device takes it up once, when `at` is
 * newer than the last one it took (or sent); after that its own switching is its own again.
 * @returns the id to switch to, or null.
 */
export function syncedDepartmentToAdopt(sync, lastAt = 0) {
  if (!sync || !sync.id || !Number.isFinite(sync.at)) return null;
  return sync.at > (Number(lastAt) || 0) ? sync.id : null;
}

/**
 * The plan leads, the switcher follows (Lj 2026-10-01): the task the day plan has you on — the running one, else
 * Up next (the first not carried from an earlier day, else the first). Same pick as Home's UP NEXT.
 */
export function planHead(plan = [], { running = null, today } = {}) {
  if (running) return running;
  const fresh = today ? plan.find((t) => carriedDays(t, today) === 0) : null;
  return fresh || plan[0] || null;
}
